import { LoadStateServerMessage } from "../types";

/**
 * Manages the loading state of an iframe and communicates with it via postMessage.
 */
export class LoadManager {
  private _iframe: HTMLIFrameElement;
  private _iframeUrl: URL;
  private _loadListener: ((data: LoadStateServerMessage) => void) | null;
  public loadState: LoadStateServerMessage["state"];

  /**
   * Creates an instance of LoadManager.
   *
   * @param {HTMLIFrameElement} iframe - The target iframe element to manage.
   */
  constructor(iframe: typeof this._iframe) {
    this.loadState = "loading";
    this._iframe = iframe;
    this._iframeUrl = new URL(iframe.src);
    this._loadListener = null;
  }

  /**
   * Initializes the LoadManager by listening for a "loadState" message from the iframe.
   * Resolves when the iframe reports that it's in a "ready" state.
   *
   * @async
   * @returns {Promise<void>} A promise that resolves when the iframe is ready.
   */
  async init() {
    console.log("Initializing Sandai Load Manager...");

    const loadPromise = new Promise<void>((resolve) => {
      this._loadListener = (data) => {
        if (!data || typeof data !== "object" || data.type !== "loadState")
          return;
        this.loadState = data.state;
        if (data.state === "ready") {
          console.log("Initialized Sandai Load Manager!");
          console.log("Sandai iframe loaded!");
          resolve();
          this.destroy();
        }
      };
      this._listenForMessage(this._loadListener);
    });
    return await loadPromise;
  }

  /**
   * Sends a message to the iframe.
   *
   * @private
   * @param {*} data - The data payload to be sent to the iframe.
   * @throws {Error} Throws an error if the iframe does not have a `contentWindow`.
   */
  _sendMessage(data: any) {
    if (this._iframe.contentWindow) {
      this._iframe.contentWindow.postMessage(data, "*");
    } else {
      throw new Error("iframe doesn't have a 'contentWindow' member");
    }
  }

  /**
   * Stores all registered message event listeners for later removal.
   *
   * @private
   */
  private _listeners: Array<(event: MessageEvent<any>) => void> = [];

  /**
   * Listens for messages from the iframe and invokes the provided callback when a message is received.
   * Only messages from the iframe's origin are processed.
   *
   * The listener is stored internally so it can be removed later via `destroy()`.
   *
   * @private
   * @param {(data: any) => void} callback - The callback function to invoke with the message data.
   */
  private _listenForMessage(callback: (data: any) => void) {
    const listener = (event: MessageEvent<any>) => {
      if (event.origin !== this._iframeUrl.origin) return;
      callback(event.data);
    };
    this._listeners.push(listener);
    window.addEventListener("message", listener);
  }

  /**
   * Cleans up all registered message event listeners previously added by `_listenForMessage`.
   * Call this method when the instance is being destroyed or no longer needs to listen to messages.
   */
  destroy() {
    console.log("Destroying Load Manager...");
    this._listeners.forEach((listener) => {
      window.removeEventListener("message", listener);
    });
    this._listeners = [];
    console.log("Destroyed Load Manager!");
  }
}
